import { UAParser } from 'ua-parser-js';

/** Visual viewport height shrink that indicates the virtual keyboard is open. */
const KEYBOARD_MIN_HEIGHT_PX = 150;

function isMobileSafari(browser: UAParser.IBrowser, device: UAParser.IDevice): boolean {
  const isAppleMobile = device.type === 'mobile' || device.type === 'tablet';

  return isAppleMobile && browser.is('Mobile Safari');
}

/**
 * Detects Safari on iPhone, iPad, or iPadOS desktop mode.
 * Excludes Chrome, Firefox, and Edge on iOS.
 *
 * @param userAgent Optional UA string for testing; defaults to `navigator.userAgent`.
 */
export function detectMobileSafari(userAgent?: string): boolean {
  if (userAgent === undefined) {
    if (typeof navigator === 'undefined') {
      return false;
    }

    const parser = new UAParser();
    const browser = parser.getBrowser();
    // withFeatureCheck() is synchronous here; IData exposes `then` for client-hints
    // chaining but is not a real Promise, so we must not treat it as async.
    const device = parser.getDevice().withFeatureCheck() as UAParser.IDevice;

    return isMobileSafari(browser, device);
  }

  const parser = new UAParser(userAgent);

  return isMobileSafari(parser.getBrowser(), parser.getDevice());
}

export function getKeyboardOffset(): number {
  const viewport = window.visualViewport;
  if (!viewport) {
    return 0;
  }

  const layoutHeight = window.innerHeight;
  const visibleHeight = viewport.height;
  const visibleTop = viewport.offsetTop;

  const keyboardHeight = layoutHeight - visibleHeight;
  if (keyboardHeight < KEYBOARD_MIN_HEIGHT_PX) {
    return 0;
  }

  return Math.max(0, keyboardHeight - visibleTop);
}

export function clampFooterOffsetToParent(footer: HTMLElement, offset: number): number {
  if (offset <= 0) {
    return 0;
  }

  const parent = footer.parentElement;
  if (!parent) {
    return offset;
  }

  const parentBottom = parent.getBoundingClientRect().bottom;
  const footerRect = footer.getBoundingClientRect();
  const currentTranslateY = new DOMMatrixReadOnly(getComputedStyle(footer).transform).m42;
  const naturalBottom = footerRect.bottom - currentTranslateY;
  const maxOffset = parentBottom - naturalBottom;

  return Math.max(0, Math.min(offset, maxOffset));
}
